"use client";

import { useEffect, useMemo, useState } from "react";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/hooks/onboarding/useOnboardingPaywallCopy";
import {
  loadOnboardingPaywallCopy,
  ONBOARDING_PAYWALL_COPY_KEY,
  type OnboardingPaywallCopy,
} from "./onboardingPaywallCopy";

const I18n = I18nUtil.createScoped(Translations);

export function useOnboardingPaywallCopy(): OnboardingPaywallCopy & {
  bodyEmphasis: boolean;
} {
  const locale = I18nUtil.getCurrentLocale();
  const fallback = useMemo(
    () => ({
      titleLine1: I18n.t("Hi, I'm Ed.", { locale }),
      titleLine2: I18n.t("I'd like to be your advisor.", { locale }),
      body: I18n.t(
        "I read 365 briefs a year, track 150+ feeds, and charge $0 commissions — clients end up +$400 ahead in month one.",
        { locale },
      ),
      ticker: [
        I18n.t("Subscribers earn +$400/mo more", { locale }),
        I18n.t("2x more red flags caught early", { locale }),
        I18n.t("3 days faster than solo investors", { locale }),
        I18n.t("40% fewer costly mistakes", { locale }),
        I18n.t("92% renew after year one", { locale }),
        I18n.t("$150/mo saved vs. going alone", { locale }),
      ],
    }),
    [locale],
  );
  const [resolved, setResolved] = useState<{
    locale: string;
    copy: OnboardingPaywallCopy;
  }>();

  useEffect(() => {
    let active = true;
    void loadOnboardingPaywallCopy(
      async () => {
        const { getValue } = await import("@features/firebase/initFirebase");
        return (await getValue(ONBOARDING_PAYWALL_COPY_KEY)).asString();
      },
      locale,
      fallback,
    ).then((copy) => {
      if (active) {
        setResolved({ locale, copy });
      }
    });
    return () => {
      active = false;
    };
  }, [locale, fallback]);

  const copy = resolved?.locale === locale ? resolved.copy : fallback;
  return { ...copy, bodyEmphasis: copy.body === fallback.body };
}
